import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/** Handles scroll positioning for SPA navigation:
 *  - hash present (e.g. `/#themes`) — scroll the matching #id into view.
 *  - hash absent (route change like `/about` → `/`) — scroll to top.
 *
 *  React Router 7 does NOT auto-scroll on either case; this hook plugs
 *  both gaps. Honors prefers-reduced-motion (jumps instead of smooth).
 *  The browser-level `scroll-behavior: smooth` on html provides a CSS
 *  fallback when scrollTo's `behavior` option isn't honored. */
export function useScrollToHash() {
  const { hash, pathname } = useLocation()

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
    const behavior: ScrollBehavior = reduced ? 'auto' : 'smooth'

    if (hash) {
      const id = hash.slice(1)
      if (!id) return
      // Defer one frame — when this fires from a cross-route nav (e.g. /about
      // → /#themes), the destination route's components haven't necessarily
      // mounted yet. A single rAF gives the new page a tick to render its
      // anchor targets before we try to find them.
      requestAnimationFrame(() => {
        const target = document.getElementById(id)
        if (!target) return
        target.scrollIntoView({ behavior, block: 'start' })
      })
    } else {
      // Plain route change — return to top. Without this, navigating from
      // a long page (landing v2) to a shorter one (/help) leaves the
      // shorter page scrolled to the previous offset, which looks broken.
      window.scrollTo({ top: 0, behavior })
    }
  }, [hash, pathname])
}
